import React from 'react';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

export interface AIProcessingStep {
  id: string;
  label: string;
  status: 'pending' | 'processing' | 'complete' | 'error';
  confidence?: number;
  error?: string;
}

interface AIProcessingStatusProps {
  steps: AIProcessingStep[];
  overallProgress: number;
  imageCount?: number;
  imageSources?: number;
}

export function AIProcessingStatus({ 
  steps, 
  overallProgress,
  imageCount,
  imageSources 
}: AIProcessingStatusProps) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold">AI Product Research</h3>
        <p className="text-sm text-muted-foreground">
          Finding product information from multiple sources...
        </p>
      </div>

      <div className="space-y-4">
        {steps.map((step) => (
          <StatusItem key={step.id} step={step} />
        ))}
      </div>

      <div className="space-y-2">
        <Progress value={overallProgress} className="h-2" />
        <p className="text-sm text-center text-muted-foreground">
          {overallProgress}% Complete
        </p>
      </div>

      {imageCount !== undefined && imageSources !== undefined && (
        <div className="text-center p-4 bg-muted/50 rounded-lg">
          <p className="text-sm">
            Found <span className="font-semibold">{imageCount} images</span> from{' '}
            <span className="font-semibold">{imageSources} sources</span>
          </p>
        </div>
      )}
    </div>
  );
}

function StatusItem({ step }: { step: AIProcessingStep }) {
  const getIcon = () => {
    switch (step.status) {
      case 'complete':
        return <Check className="h-4 w-4 text-green-600" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />;
    }
  };

  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{getIcon()}</div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className={cn(
            "text-sm font-medium",
            step.status === 'complete' && "text-green-600",
            step.status === 'processing' && "text-primary",
            step.status === 'error' && "text-red-600"
          )}>
            {step.label}
          </p>
          {step.confidence !== undefined && step.status === 'complete' && (
            <span className="text-xs text-muted-foreground">
              ({Math.round(step.confidence * 100)}% confidence)
            </span>
          )}
        </div>
        {step.error && (
          <p className="text-xs text-red-600 mt-1">{step.error}</p>
        )}
      </div>
    </div>
  );
}